"use client"

import { MessageTextSquare01 } from "@untitledui/icons"
import { type ReactNode, useState } from "react"

import { ButtonUtility } from "@/components/ui/button-utility"
import type { WarmStartFieldNote } from "@/types"

import { FieldNote, FieldNoteEditor } from "../../_components/field-note"

/**
 * How a margin note persists when the surface is editable (the coordinator's
 * editor). `commit` upserts the note body for this anchor; `remove` deletes
 * it. Built per-anchor by the page (see `noteEditorFor` in playlists/edit).
 */
export interface MarginNoteEditor {
  commit: (body: string) => void
  remove: () => void
}

/** What a margin note is pinned to — a singleton field or a track (by post id). */
export interface NoteAnchor {
  field: "title" | "description" | "track"
  ref?: string
}

/**
 * One shared geometry so the coordinator's hand reads as a single continuous
 * margin running down the whole "Make it yours" editor — beside the title, the
 * description, and every track. Main content fills column 1; the margin (the
 * notes) sits in a fixed column 2, top-aligned to its row so a note lines up
 * with the field or card it annotates. Below `lg` there's no room for a margin,
 * so the note folds beneath its row (the note is always the second child, so it
 * stacks underneath in source order).
 */
export const marginRowClass =
  "grid grid-cols-1 gap-x-10 gap-y-3 lg:grid-cols-[minmax(0,1fr)_17rem] lg:items-start lg:gap-y-0"

/** A main-column row with an optional margin aside (the notes column). */
export function MarginRow({
  children,
  aside,
}: {
  children: ReactNode
  aside?: ReactNode
}) {
  return (
    <div className={marginRowClass}>
      <div className="min-w-0">{children}</div>
      <div className="lg:self-start">{aside}</div>
    </div>
  )
}

/**
 * The growth manager's marginalia for one anchor (a field or a track). Empty
 * anchors render nothing — the margin simply stays blank beside that row, the
 * way an unannotated paragraph does.
 */
export function MarginNotes({
  notes,
  authorName,
  avatarUrl,
  currentValue,
  editor,
}: {
  notes: WarmStartFieldNote[]
  authorName: string
  avatarUrl?: string | null
  currentValue: string
  /**
   * Present only on the coordinator surface: the notes render as edit surfaces
   * and an empty anchor offers the speech-bubble "add a note" affordance.
   */
  editor?: MarginNoteEditor
}) {
  // Locally true while an empty note is being composed from the add button;
  // committing persists it, blurring empty collapses it. The composing surface
  // stays mounted (holding its draft) until the persisted note lands in
  // `notes` — then this resets so a later delete re-offers the button.
  const [composing, setComposing] = useState(false)
  if (composing && notes.length > 0) setComposing(false)

  if (editor) {
    if (notes.length === 0 && !composing) {
      return (
        <ButtonUtility
          size="sm"
          color="tertiary"
          tooltip="Add a note for the recipient"
          icon={MessageTextSquare01}
          onClick={() => setComposing(true)}
        />
      )
    }
    return (
      <div className="flex flex-col gap-3">
        {notes.map((note, i) => (
          <FieldNoteEditor
            key={i}
            authorName={authorName}
            avatarUrl={avatarUrl}
            body={note.body}
            onCommit={editor.commit}
            onRemove={editor.remove}
          />
        ))}
        {notes.length === 0 && composing && (
          <FieldNoteEditor
            autoFocus
            authorName={authorName}
            avatarUrl={avatarUrl}
            body=""
            onCommit={editor.commit}
            onRemove={() => setComposing(false)}
          />
        )}
      </div>
    )
  }

  if (notes.length === 0) return null
  return (
    <div className="flex flex-col gap-3">
      {notes.map((note, i) => (
        <FieldNote
          key={i}
          authorName={authorName}
          avatarUrl={avatarUrl}
          body={note.body}
          seededValue={note.seeded_value}
          currentValue={currentValue}
        />
      ))}
    </div>
  )
}
